import { useCallback, useEffect, useState } from "react";
import { createViewBusinessPartnerDetails } from "@/ui/views";
import { fetchAllCoaMappings } from "@/useCases/coaMappings";
import { fetchAllPaginated } from "@/utils/fetchAllPaginated";
import type { TCompositionDeps } from "./types";

export const wireBusinessPartnerDetails = ({
    store: _store,
    useAppStore,
    api,
}: TCompositionDeps) => {
    const ViewBusinessPartnerDetails = createViewBusinessPartnerDetails({
        useCases: () => {
            const currentUser = useAppStore((state) => state.user);
            const canEdit = currentUser?.permissions.includes("business_partner_edit") ?? false;
            const canDeactivate =
                currentUser?.permissions.includes("business_partner_deactivate") ?? false;
            const canEditEmployees =
                currentUser?.permissions.includes("business_partner_employees_edit") ?? false;
            const canUploadCoa =
                currentUser?.permissions.includes("business_partner_coa_upload") ?? false;
            const canEditCoaLines =
                currentUser?.permissions.includes("business_partner_coa_line_edit") ?? false;
            const [businessPartnerState, setBusinessPartnerState] = useState<{
                data: {
                    id: string;
                    companyId: string;
                    name: string;
                    code: string;
                    businessPartnerType: "service_provider" | "portfolio_company";
                    isActive: boolean;
                    contactEmail: string | null;
                    canUploadSupportingFiles: boolean;
                    coaMappingMode: "single" | "multi_criteria";
                    isTest: boolean;
                    employees: Array<{ id: string; display_name: string; email: string }>;
                    createdAt: string;
                    updatedAt: string;
                } | null;
                loading: boolean;
                error: string | null;
            }>({ data: null, loading: false, error: null });

            const [coaMappingsState, setCoaMappingsState] = useState<{
                data: Array<{
                    id: string;
                    thirdAccountCode: string;
                    thirdAccountCode2: string | null;
                    isFallback: boolean;
                    thirdAccountName: string;
                    yardiAccountId: string;
                    yardiAccountCode: string | null;
                    yardiAccountName: string | null;
                    level: "A" | "P" | "C";
                    isInForce: boolean;
                    isActive: boolean;
                    createdAt: string;
                    updatedAt: string;
                }> | null;
                loading: boolean;
            }>({ data: null, loading: false });

            const [chartOfAccountsList, setChartOfAccountsList] = useState<
                Array<{
                    id: string;
                    number: string;
                    shortName: string;
                    isProhibited: boolean;
                    isActive: boolean;
                }>
            >([]);

            const [bpUsers, setBpUsers] = useState<{
                data: Array<{ id: string; displayName: string; email: string }> | null;
                loading: boolean;
            }>({ data: null, loading: true });

            const onMount = useCallback(async (businessPartnerId: string) => {
                setBusinessPartnerState({ data: null, loading: true, error: null });
                setCoaMappingsState({ data: null, loading: true });
                setBpUsers({ data: null, loading: true });

                api.users
                    .list({ page_size: 100, business_partners: true })
                    .then((userData) => {
                        setBpUsers({
                            data: userData.items.map((u: any) => ({
                                id: u.id,
                                displayName: u.display_name,
                                email: u.email,
                            })),
                            loading: false,
                        });
                    })
                    .catch(() => {
                        setBpUsers({ data: [], loading: false });
                    });

                // Load the full master Chart of Accounts (all pages) so every
                // account is selectable in the mapping dropdowns, not just the
                // first page. The list endpoint caps page_size at 1000.
                const fetchAllChartOfAccounts = () =>
                    fetchAllPaginated((page, page_size) =>
                        api.chartOfAccounts.list({ page, page_size }),
                    );

                try {
                    try {
                        const sp = await api.serviceProviders.get(businessPartnerId);
                        setBusinessPartnerState({
                            data: {
                                id: sp.id,
                                companyId: sp.company_id,
                                name: sp.name,
                                code: sp.code,
                                businessPartnerType: sp.business_partner_type,
                                isActive: sp.is_active,
                                contactEmail: sp.contact_email,
                                canUploadSupportingFiles: sp.can_upload_supporting_files,
                                coaMappingMode: sp.coa_mapping_mode ?? "single",
                                isTest: sp.is_test,
                                employees: (sp.employees ?? []).map((e: any) => ({
                                    id: e.id,
                                    display_name: e.display_name,
                                    email: e.email,
                                })),
                                createdAt: sp.created_at,
                                updatedAt: sp.updated_at,
                            },
                            loading: false,
                            error: null,
                        });

                        const [mappingItems, coaListItems] = await Promise.all([
                            fetchAllCoaMappings(api.serviceProviders, businessPartnerId),
                            fetchAllChartOfAccounts(),
                        ]);
                        setChartOfAccountsList(
                            coaListItems.map((a) => ({
                                id: a.id,
                                number: a.number,
                                shortName: a.short_name,
                                isProhibited: a.is_prohibited,
                                isActive: a.is_active,
                            })),
                        );
                        setCoaMappingsState({
                            data: mappingItems.map((m: any) => ({
                                id: m.id,
                                thirdAccountCode: m.third_account_code,
                                thirdAccountCode2: m.third_account_code_2 ?? null,
                                isFallback: m.is_fallback ?? false,
                                thirdAccountName: m.third_account_name,
                                yardiAccountId: m.yardi_account_id,
                                yardiAccountCode: m.yardi_account_code,
                                yardiAccountName: m.yardi_account_name,
                                level: m.level as "A" | "P" | "C",
                                isInForce: m.is_in_force,
                                isActive: m.is_active,
                                createdAt: m.created_at,
                                updatedAt: m.updated_at,
                            })),
                            loading: false,
                        });
                    } catch {
                        const pc = await api.portfolioCompanies.get(businessPartnerId);
                        setBusinessPartnerState({
                            data: {
                                id: pc.id,
                                companyId: pc.company_id,
                                name: pc.name,
                                code: pc.code,
                                businessPartnerType: "portfolio_company",
                                isActive: pc.is_active,
                                contactEmail: null,
                                canUploadSupportingFiles: pc.can_upload_supporting_files ?? true,
                                coaMappingMode: pc.coa_mapping_mode ?? "single",
                                isTest: pc.is_test ?? false,
                                employees: (pc.employees ?? []).map((e: any) => ({
                                    id: e.id,
                                    display_name: e.display_name,
                                    email: e.email,
                                })),
                                createdAt: pc.created_at,
                                updatedAt: pc.updated_at,
                            },
                            loading: false,
                            error: null,
                        });

                        const [mappingItems, coaListItems] = await Promise.all([
                            fetchAllCoaMappings(api.portfolioCompanies, businessPartnerId),
                            fetchAllChartOfAccounts(),
                        ]);
                        setChartOfAccountsList(
                            coaListItems.map((a) => ({
                                id: a.id,
                                number: a.number,
                                shortName: a.short_name,
                                isProhibited: a.is_prohibited,
                                isActive: a.is_active,
                            })),
                        );
                        setCoaMappingsState({
                            data: mappingItems.map((m: any) => ({
                                id: m.id,
                                thirdAccountCode: m.third_account_code,
                                thirdAccountCode2: m.third_account_code_2 ?? null,
                                isFallback: m.is_fallback ?? false,
                                thirdAccountName: m.third_account_name,
                                yardiAccountId: m.yardi_account_id,
                                yardiAccountCode: m.yardi_account_code,
                                yardiAccountName: m.yardi_account_name,
                                level: m.level as "A" | "P" | "C",
                                isInForce: m.is_in_force,
                                isActive: m.is_active,
                                createdAt: m.created_at,
                                updatedAt: m.updated_at,
                            })),
                            loading: false,
                        });
                    }
                } catch {
                    setBusinessPartnerState({
                        data: null,
                        loading: false,
                        error: "Failed to load business partner details",
                    });
                    setCoaMappingsState({ data: [], loading: false });
                }
            }, []);

            const onUnmount = useCallback(() => {
                setBusinessPartnerState({ data: null, loading: false, error: null });
                setCoaMappingsState({ data: null, loading: false });
            }, []);

            const onUpdateCoAMapping = useCallback(
                async (
                    businessPartnerId: string,
                    businessPartnerType: "service_provider" | "portfolio_company",
                    mappingId: string,
                    data: {
                        level?: "A" | "P" | "C";
                        is_active?: boolean;
                        yardi_account_id?: string;
                        is_in_force?: boolean;
                    },
                ) => {
                    if (businessPartnerType === "service_provider") {
                        await api.serviceProviders.updateCoAMapping(
                            businessPartnerId,
                            mappingId,
                            data,
                        );
                    } else {
                        await api.portfolioCompanies.updateCoAMapping(
                            businessPartnerId,
                            mappingId,
                            data,
                        );
                    }
                    await onMount(businessPartnerId);
                },
                [onMount],
            );

            const onCreateCoAMapping = useCallback(
                async (
                    businessPartnerId: string,
                    businessPartnerType: "service_provider" | "portfolio_company",
                    data: {
                        third_account_code: string;
                        third_account_name: string;
                        yardi_account_id: string;
                        level: "A" | "P" | "C";
                    },
                ) => {
                    if (businessPartnerType === "service_provider") {
                        await api.serviceProviders.createCoAMapping(businessPartnerId, data);
                    } else {
                        await api.portfolioCompanies.createCoAMapping(businessPartnerId, data);
                    }
                    await onMount(businessPartnerId);
                },
                [onMount],
            );

            const onDeleteCoAMapping = useCallback(
                async (
                    businessPartnerId: string,
                    businessPartnerType: "service_provider" | "portfolio_company",
                    mappingId: string,
                ) => {
                    if (businessPartnerType === "service_provider") {
                        await api.serviceProviders.deleteCoAMapping(businessPartnerId, mappingId);
                    } else {
                        await api.portfolioCompanies.deleteCoAMapping(businessPartnerId, mappingId);
                    }
                    await onMount(businessPartnerId);
                },
                [onMount],
            );

            const [allBusinessPartnerNames, setAllBusinessPartnerNames] = useState<string[]>([]);

            useEffect(() => {
                Promise.all([
                    api.serviceProviders.list({ skip: 0, limit: 1000 }),
                    api.portfolioCompanies.list({ skip: 0, limit: 1000 }),
                ])
                    .then(([spRes, pcRes]) => {
                        setAllBusinessPartnerNames([
                            ...spRes.items.map((sp: any) => sp.name as string),
                            ...pcRes.items.map((pc: any) => pc.name as string),
                        ]);
                    })
                    .catch(() => {});
            }, []);

            const onUpdateBusinessPartner = useCallback(
                async (
                    businessPartnerId: string,
                    businessPartnerType: "service_provider" | "portfolio_company",
                    data: {
                        name?: string;
                        canUploadSupportingFiles?: boolean;
                        coaMappingMode?: "single" | "multi_criteria";
                        isActive?: boolean;
                        isTest?: boolean;
                        employeeIds?: string[];
                    },
                ) => {
                    const isPC = businessPartnerType === "portfolio_company";
                    const loaded = businessPartnerState.data;

                    // Core fields — only call update() when the user has edit permission
                    // AND at least one edit-gated field actually changed from the loaded value.
                    if (canEdit) {
                        const nameChanged = data.name !== undefined && data.name !== loaded?.name;
                        const uploadChanged =
                            data.canUploadSupportingFiles !== undefined &&
                            data.canUploadSupportingFiles !== loaded?.canUploadSupportingFiles;
                        const testChanged =
                            data.isTest !== undefined && data.isTest !== loaded?.isTest;
                        const coaModeChanged =
                            data.coaMappingMode !== undefined &&
                            data.coaMappingMode !== loaded?.coaMappingMode;

                        if (nameChanged || uploadChanged || testChanged || coaModeChanged) {
                            // Only send the mode when it actually changed: the API
                            // rejects "multi_criteria" while the environment flag is
                            // off, so echoing an unchanged value would 400 saves of
                            // unrelated fields on a partner whose data came from
                            // another environment.
                            const coaMappingMode = coaModeChanged ? data.coaMappingMode : undefined;
                            if (isPC) {
                                await api.portfolioCompanies.update(businessPartnerId, {
                                    name: data.name,
                                    can_upload_supporting_files: data.canUploadSupportingFiles,
                                    coa_mapping_mode: coaMappingMode,
                                    is_test: data.isTest,
                                });
                            } else {
                                await api.serviceProviders.update(businessPartnerId, {
                                    name: data.name,
                                    can_upload_supporting_files: data.canUploadSupportingFiles,
                                    coa_mapping_mode: coaMappingMode,
                                    is_test: data.isTest,
                                });
                            }
                        }
                    }

                    // Active status — only call when user has deactivate permission
                    // AND the active state actually changed from the loaded value.
                    if (
                        canDeactivate &&
                        data.isActive !== undefined &&
                        data.isActive !== loaded?.isActive
                    ) {
                        if (isPC) {
                            if (data.isActive) {
                                await api.portfolioCompanies.activate(businessPartnerId);
                            } else {
                                await api.portfolioCompanies.deactivate(businessPartnerId);
                            }
                        } else {
                            if (data.isActive) {
                                await api.serviceProviders.activate(businessPartnerId);
                            } else {
                                await api.serviceProviders.deactivate(businessPartnerId);
                            }
                        }
                    }

                    // Employees — only call when user has employees-edit permission
                    // AND the employee set actually changed from the loaded value.
                    if (canEditEmployees && data.employeeIds !== undefined) {
                        const loadedIds = (loaded?.employees ?? []).map((e) => e.id);
                        const newIds = data.employeeIds;
                        const employeesChanged =
                            loadedIds.length !== newIds.length ||
                            newIds.some((id) => !loadedIds.includes(id)) ||
                            loadedIds.some((id) => !newIds.includes(id));

                        if (employeesChanged) {
                            if (isPC) {
                                await api.portfolioCompanies.setEmployees(
                                    businessPartnerId,
                                    data.employeeIds,
                                );
                            } else {
                                await api.serviceProviders.setEmployees(
                                    businessPartnerId,
                                    data.employeeIds,
                                );
                            }
                        }
                    }

                    await onMount(businessPartnerId);
                },
                [onMount, businessPartnerState.data, canEdit, canDeactivate, canEditEmployees],
            );

            return {
                onMount,
                onUnmount,
                useBusinessPartnerState: () => businessPartnerState,
                useCoAMappings: () => coaMappingsState,
                onCreateCoAMapping,
                onUpdateCoAMapping,
                onDeleteCoAMapping,
                useChartOfAccountsList: () => chartOfAccountsList,
                onUpdateBusinessPartner,
                useUsers: () => bpUsers,
                useExistingNames: () => allBusinessPartnerNames,
                canEdit,
                canDeactivate,
                canEditEmployees,
                canUploadCoa,
                canEditCoaLines,
                // Environment flag, not a permission: the feature does not exist
                // in this deployment at all when it is off.
                multiCriteriaCoaAvailable:
                    currentUser?.environmentFeatureFlags?.multi_criteria_coa_enabled ?? false,
            };
        },
        store: _store,
    });

    return { ViewBusinessPartnerDetails };
};
